import { stringifyMap } from './helpers';
import { renderWrappedImage } from '@/utils/articles/renderers/wrapped-image';
import { renderArticleBanner } from '@/utils/articles/renderers/article-banner';
import { renderAssetReference } from '@/utils/articles/renderers/sys-assets';

let headingIndex = 0; // Initialize a counter for the h2 headings
const outboundLinkAttrs = [
  'target="_blank"',
  'rel="external noopener noreferrer"'
];

const normalizeHeadingChild = (html = '') => {
  const wrapperMatch = html.match(/^<span([^>]*)>([\s\S]*)<\/span>$/i);

  if (!wrapperMatch) {
    return { className: '', child: html };
  }

  const [, wrapperAttrs, wrapperChild] = wrapperMatch;
  const classMatch = wrapperAttrs.match(/\bclass="([^"]*)"/i);

  return {
    className: classMatch?.[1]?.trim() || '',
    child: wrapperChild
  };
};

export const customHTMLSerializer = {
  customElementTypes: {
    'social-embed': (attrs, child) => {
      return `<social-embed${attrs}>${child}</social-embed>`;
    },
    h2: (_attrs, child) => {
      const id = `heading-${headingIndex++}`;
      return `<h2 id="${id}" class="h4 jump-offset mt-12 mb-4">${child}</h2>`;
    },
    h3: (_attrs, child) => {
      const normalized = normalizeHeadingChild(child);
      const className = ['sh9', 'mt-6', 'mb-4', normalized.className]
        .filter(Boolean)
        .join(' ');

      return `<h3 class="${className}">${normalized.child}</h3>`;
    },
    h4: (_attrs, child) => {
      return `<h4 class="text-xl font-semibold mt-6 mb-4">${child}</h4>`;
    },
    h5: (_attrs, child) => {
      return `<h5 class="text-xl font-semibold mt-6 mb-4">${child}</h5>`;
    },
    h6: (_attrs, child) => {
      return `<h6 class="text-xl font-semibold mt-6 mb-4">${child}</h6>`;
    },
    p: (_attrs, child) => {
      return `<p class="mb-4">${child}</p>`;
    },
    a: (attrs, child) => {
      // attrs contains target="_blank" if you check "Open link in new tab
      const isOutboundLink = attrs.includes('target="_blank"');
      const attrsArr = attrs.split(' ');
      const attrsMap = new Map();

      // Parse attributes into a key-value map
      attrsArr.forEach((attr) => {
        const match = attr.match(/([^=]+)="([^"]+)"/);

        if (match && !match[1].startsWith('url')) {
          const [, key, value] = match;
          attrsMap.set(key, value);
        }
      });

      if (!isOutboundLink) {
        return `<a ${stringifyMap(attrsMap)} class="underline hover:text-cozy-cognac cursor-pointer">${child}</a>`;
      }

      return `<a ${stringifyMap(attrsMap)} class="hover:text-cozy-cognac cursor-pointer relative" ${outboundLinkAttrs.join(' ')}>
                <span class="underline">${child}</span>
                <span class="sr-only">opens in new window</span>
                <svg aria-hidden="true" focusable="false" class="inline" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.0002 5H8.2002C7.08009 5 6.51962 5 6.0918 5.21799C5.71547 5.40973 5.40973 5.71547 5.21799 6.0918C5 6.51962 5 7.08009 5 8.2002V15.8002C5 16.9203 5 17.4801 5.21799 17.9079C5.40973 18.2842 5.71547 18.5905 6.0918 18.7822C6.5192 19 7.07899 19 8.19691 19H15.8031C16.921 19 17.48 19 17.9074 18.7822C18.2837 18.5905 18.5905 18.2839 18.7822 17.9076C19 17.4802 19 16.921 19 15.8031V14M20 9V4M20 4H15M20 4L13 11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </a>`;
    },
    img: (attrs) => {
      const attrsArr = attrs.match(/([a-zA-Z0-9_-]+)="([^"]*)"/g) || [];
      const attrsMap = new Map();
      let caption = '';

      attrsArr.forEach((attr) => {
        const match = attr.match(/([a-zA-Z0-9_-]+)="([^"]*)"/);
        if (match) {
          const [, key, value] = match;
          if (key === 'caption') caption = value;
          if (key === 'url' || key === 'position' || key === 'caption') return;
          attrsMap.set(key, value);
        }
      });

      const mainTag = `<figure class="mb-6">
        <img ${stringifyMap(attrsMap)} class="w-full h-auto" />
        ${caption ? `<figcaption class="text-sm text-dark-grey-text mt-2">${caption}</figcaption>` : ''}
      </figure>`;

      // If you add a link to the image in contentstack, it gets the anchorLink attribute
      if (attrsMap.has('anchorLink')) {
        const anchorLink = attrsMap.get('anchorLink');
        const target = attrsMap.get('target') || '';
        return `<a href="${anchorLink}" ${target ? `target="${target}"` : ''} class="block">${mainTag}</a>`;
      }
      return mainTag;
    },
    ul: (_attrs, child) => {
      return `<ul class="list-disc mb-6 pl-4">${child}</ul>`;
    },
    ol: (_attrs, child) => {
      return `<ol class="list-decimal mb-6 pl-4">${child}</ol>`;
    },
    li: (_attrs, child) => {
      return `<li class="mb-2">${child}</li>`;
    },
    div: (_attrs, child) => `<div>${child}</div>`,
    u: (_attrs, child) => `<span class="underline">${child}</span>`,
    table: (_attrs, child) => `<table class="w-full">${child}</table>`,
    td: (_attrs, child) =>
      `<td class="w-1/2 px-6 pb-4 align-top">${child}</td>`,
    blockquote: (_attrs, child) => `<blockquote>${child}</blockquote>`,
    reference: (_attrs, _child, jsonBlock) => {
      const contentType = jsonBlock?.attrs?.['content-type-uid'];
      if (!contentType) return '';

      switch (contentType) {
        case 'sys_assets':
          return renderAssetReference(_attrs, _child, jsonBlock);
        case 'wrapped_image':
          return renderWrappedImage(_attrs, _child, jsonBlock);
        case 'article_banner':
          return renderArticleBanner(_attrs, _child, jsonBlock);
        default:
          return '';
      }
    },
    embed: (_attrs, child) => {
      return `
        <div class="relative w-full" style="padding-bottom: 56.25%;">
          <iframe
            ${_attrs}
            class="absolute top-0 left-0 w-full h-full"
            frameborder="0" 
            allowfullscreen
          >
            ${child}
          </iframe>
        </div>
      `;
    }
  }
};
